/**
 * Rule-based letter-template recommender.
 *
 * Given the current Contact record fields (Contact Type, Contact Mode,
 * Contact Status) and the list of existing letter templates, this returns the
 * templates whose *name* looks relevant to the contact — e.g. a "Post clinic
 * follow-up" contact sent by "Letter" surfaces a "Post Clinic Follow-up Letter"
 * template.
 *
 * The comparison is a lightweight token overlap between each contact field
 * value and the template name. It is intentionally deterministic (no ML, no
 * network) so the behaviour is predictable and unit-testable.
 */

// True function words only — everything else (letter, email, clinic, patient,
// follow, risk, …) carries a meaningful signal and must survive tokenisation.
const STOPWORDS = new Set([
  'the',
  'a',
  'an',
  'of',
  'for',
  'to',
  'with',
  'and',
  'or',
  'in',
  'on',
  'at',
  'by',
  'from',
  'into',
  're'
]);

// Contact Type is the strongest hint about *which* template applies; Mode
// distinguishes letter vs email vs call; Status is the weakest nudge.
const FIELDS = [
  { key: 'contactType', label: 'Contact Type', weight: 3 },
  { key: 'contactMode', label: 'Contact Mode', weight: 2 },
  { key: 'contactStatus', label: 'Contact Status', weight: 1 }
];

// Two stems that share a prefix of at least this many characters are treated
// as the same root — catches inflections a naive plural-strip misses, e.g.
// "cancelled" ⇄ "cancellation", "scheduled" ⇄ "schedule", "consult" ⇄
// "consultation". Kept high enough to avoid spurious hits (e.g. "pre" ⇄
// "predictive" share only 3).
const MIN_SHARED_PREFIX = 5;

/** Crude singularisation so "patients" matches "patient", "letters" "letter". */
function stem(token) {
  return token.length >= 4 && token.endsWith('s') ? token.slice(0, -1) : token;
}

function commonPrefixLength(a, b) {
  const limit = Math.min(a.length, b.length);
  let i = 0;
  while (i < limit && a[i] === b[i]) i++;
  return i;
}

/** Same word or same root (long shared prefix). */
function stemsMatch(a, b) {
  return a === b || commonPrefixLength(a, b) >= MIN_SHARED_PREFIX;
}

/**
 * Break a string into a de-duplicated set of significant word stems.
 * @param {string} value
 * @returns {Set<string>}
 */
function stemSet(value) {
  const stems = new Set();
  String(value == null ? '' : value)
    .toLowerCase()
    .replace(/['’]/g, '') // patient's -> patients
    .split(/[^a-z0-9]+/)
    .forEach((word) => {
      if (word.length >= 2 && !STOPWORDS.has(word)) stems.add(stem(word));
    });
  return stems;
}

/**
 * Score one template against the contact context.
 * @returns {{ score: number, reasons: Array<{field:string,label:string,matched:string[]}> }}
 */
function scoreTemplate(nameStems, fieldStems) {
  let score = 0;
  const reasons = [];
  const nameList = [...nameStems];

  for (const field of FIELDS) {
    const stems = fieldStems[field.key];
    if (!stems || stems.size === 0) continue;

    const matched = [];
    stems.forEach((s) => {
      if (nameList.some((nameStem) => stemsMatch(nameStem, s))) matched.push(s);
    });
    if (matched.length === 0) continue;

    // Every overlapping word contributes; a name that contains the *whole*
    // field value (all its stems) earns an extra boost so exact-intent
    // templates float to the top.
    score += field.weight * matched.length;
    if (matched.length === stems.size) score += field.weight;

    reasons.push({ field: field.key, label: field.label, matched });
  }

  return { score, reasons };
}

/**
 * @param {Array<{_id?:string,name?:string,content?:string}>} templates
 * @param {{contactType?:string,contactMode?:string,contactStatus?:string}} context
 * @param {{max?:number}} [options]
 * @returns {{ recommended: Array<{template:object,score:number,reasons:object[]}>, others: object[] }}
 */
export function recommendTemplates(templates, context = {}, options = {}) {
  const list = Array.isArray(templates) ? templates : [];

  const fieldStems = {};
  for (const field of FIELDS) {
    fieldStems[field.key] = stemSet(context[field.key]);
  }
  const hasContext = FIELDS.some((f) => fieldStems[f.key].size > 0);

  if (!hasContext) {
    return { recommended: [], others: [...list] };
  }

  const scored = list.map((template, index) => {
    const nameStems = stemSet(template && template.name);
    const { score, reasons } = scoreTemplate(nameStems, fieldStems);
    return { template, score, reasons, index };
  });

  const matches = scored
    .filter((entry) => entry.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const nameA = (a.template && a.template.name) || '';
      const nameB = (b.template && b.template.name) || '';
      const byName = nameA.localeCompare(nameB);
      return byName !== 0 ? byName : a.index - b.index;
    });

  const capped =
    typeof options.max === 'number' && options.max >= 0
      ? matches.slice(0, options.max)
      : matches;

  const recommendedTemplates = new Set(capped.map((entry) => entry.template));

  return {
    recommended: capped.map(({ template, score, reasons }) => ({
      template,
      score,
      reasons
    })),
    others: list.filter((template) => !recommendedTemplates.has(template))
  };
}

export default recommendTemplates;
